import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Badge } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { solid } from "@fortawesome/fontawesome-svg-core/import.macro";

import { RootState, disableIdleLockSwitch, enableIdleLockSwitch } from "../../../../globals";
import { classroomStatus } from "../../../../constants";
import { getAccessPermissions } from "../../../../common";
import Card from "../../../../components/card";
import { BarraFiltros, BuscadorBarra, FiltroMulti, coincide } from "../../../../components/buscadorBarra/buscadorBarra";
import BuildingsService from "../../../../services/campus/buildings.service";
import ClassroomsService from "../../../../services/campus/classrooms.service";
import CatalogService from "../../../../services/catalog.service";
import SimpleDataService from "../../../../services/simpleData.service";
import useEsTelefono from "../../../../utils/esTelefono";
import { ConfirmarBorrado, MenuFila } from "../../managment/calendarioAcademico/piezas";
import { Aula, AulaVentana, Categoria, Edificio, EdificioVentana, Estatus, motivoError, nombreCategoria } from "./ventanas";
import OcupacionAulas from "../../academy/calendario/ocupacionAulas";
import "../../managment/enrollmentRequests/filtrosMatriculas.css";
import "../../managment/calendarioAcademico/calendarioAcademico.css";

/*
	Aulas (Horarios Claros, 2026-11-01): Edificios y Aulas en una sola pantalla, las aulas agrupadas bajo el encabezado de su
	edificio. Las rutas viejas /classrooms y /buildings abren aquí. Mismos servicios y mismos campos que las pantallas viejas.
*/

const nombreAula = (a: Aula, b: Aula) => a.name.localeCompare(b.name, "es", { numeric: true, sensitivity: "base" });
const inactiva = (a: Aula) => Number(a.status_id) !== classroomStatus.ACTIVE;

const AulasController: React.FC = () => {
	const dispatch = useDispatch();
	const movil = useEsTelefono();
	const usuario: any = useSelector((s: RootState) => s.userProfile.value);
	const sessionToken: string | null = useSelector((s: RootState) => s.sessionToken.value);
	const currentUserCampus: any = useSelector((s: RootState) => s.currentUserCampus.value);
	const campusRef: any = useSelector((s: RootState) => s.campusRef.value);
	const campusId: number = (campusRef && currentUserCampus != null && campusRef[currentUserCampus]?.id) || 0;

	const pAulas: any = useMemo(() => getAccessPermissions(usuario, "classrooms"), [usuario]);
	const pEdificios: any = useMemo(() => getAccessPermissions(usuario, "buildings"), [usuario]);
	// Ocupación (Calendario Claro, 2026-11-02): pestaña junto a la lista; Online (5) cobra en lempiras como Honduras.
	const [pestana, setPestana] = useState<"lista" | "ocupacion">("lista");
	const verOcupacion = !!pAulas.read;
	const abreCalendario = !!getAccessPermissions(usuario, "dailyView").read;
	const countryId: number | null = campusId === 5 ? 2 : (campusRef && currentUserCampus != null ? campusRef[currentUserCampus]?.company?.country_id ?? null : null);

	const [edificios, setEdificios] = useState<Edificio[] | null>(null);
	const [aulas, setAulas] = useState<Aula[]>([]);
	const [categorias, setCategorias] = useState<Categoria[]>([]);
	const [estatuses, setEstatuses] = useState<Estatus[]>([]);
	const [error, setError] = useState<string | null>(null);
	const [aviso, setAviso] = useState<string | null>(null);
	const [q, setQ] = useState("");
	const [filtroEdificios, setFiltroEdificios] = useState<number[]>([]);
	const [filtroCategorias, setFiltroCategorias] = useState<number[]>([]);

	// null = cerrada; "nueva" = alta; un Aula = edición.
	const [ventanaAula, setVentanaAula] = useState<Aula | "nueva" | null>(null);
	const [ventanaEdificio, setVentanaEdificio] = useState<Edificio | "nuevo" | null>(null);
	const [borrandoAula, setBorrandoAula] = useState<Aula | null>(null);
	const [borrandoEdificio, setBorrandoEdificio] = useState<Edificio | null>(null);
	const [errorBorrar, setErrorBorrar] = useState<string | null>(null);

	const cargar = useCallback(async () => {
		if (!campusId) return;
		dispatch(enableIdleLockSwitch());
		try {
			const [rEdificios, rAulas, rCategorias, rEstatuses] = await Promise.all([
				BuildingsService.searchBuildings(campusId, undefined, null),
				ClassroomsService.searchClassrooms(campusId, undefined, undefined),
				SimpleDataService.getRecords("categories"),
				CatalogService.getClassroomStatuses(sessionToken),
			]);
			const lista = (r: any) => (Array.isArray(r.data) ? r.data : (r.data?.data ?? []));
			setEdificios(lista(rEdificios));
			setAulas(lista(rAulas));
			setCategorias([...lista(rCategorias)].sort((a: Categoria, b: Categoria) => a.name.localeCompare(b.name, "es")));
			setEstatuses(lista(rEstatuses));
			setError(null);
		} catch (e) {
			setError("No se pudieron cargar las aulas.");
			setEdificios((actual) => actual ?? []);
		} finally {
			dispatch(disableIdleLockSwitch());
		}
	}, [campusId, dispatch, sessionToken]);

	useEffect(() => { setEdificios(null); cargar(); }, [cargar]);

	const hayFiltros = q !== "" || filtroEdificios.length > 0 || filtroCategorias.length > 0;

	const grupos = useMemo(() => {
		const porEdificio = (edificios || []).slice().sort((a, b) => a.name.localeCompare(b.name, "es", { numeric: true, sensitivity: "base" }));
		return porEdificio
			.filter((e) => filtroEdificios.length < 1 || filtroEdificios.includes(e.id))
			.map((e) => ({
				edificio: e,
				aulas: aulas
					.filter((a) => Number(a.building_id) === e.id)
					.filter((a) => filtroCategorias.length < 1 || (a.categories || []).some((c) => filtroCategorias.includes(c.id)))
					.filter((a) => coincide(q, a.name, a.description, e.name, (a.categories || []).map((c) => c.name).join(" ")))
					.sort(nombreAula),
			}))
			// Con buscador o filtros solo salen los edificios que tienen algo que mostrar.
			.filter((g) => !(q !== "" || filtroCategorias.length > 0) || g.aulas.length > 0);
	}, [edificios, aulas, q, filtroEdificios, filtroCategorias]);

	const opcionesEdificio = useMemo(() => (edificios || []).map((e) => ({ id: e.id, name: e.name })), [edificios]);
	const opcionesCategoria = useMemo(() => categorias.map((c) => ({ id: c.id, name: nombreCategoria(c.name) })), [categorias]);

	const guardada = async (texto: string) => {
		setVentanaAula(null);
		setVentanaEdificio(null);
		setAviso(texto);
		await cargar();
	};

	const pedirBorrarAula = (a: Aula) => { setErrorBorrar(null); setBorrandoAula(a); };
	const pedirBorrarEdificio = (e: Edificio) => { setErrorBorrar(null); setBorrandoEdificio(e); };

	const borrarAula = async () => {
		if (!borrandoAula) return;
		setErrorBorrar(null);
		dispatch(enableIdleLockSwitch());
		try {
			await ClassroomsService.removeClassrooms(campusId, [borrandoAula.id]);
			setBorrandoAula(null);
			setVentanaAula(null);
			setAviso("Aula Borrada");
			await cargar();
		} catch (e) {
			setErrorBorrar(motivoError(e, "No se pudo borrar: hay horarios u otra información que usan esta aula.", "No se pudo borrar: hay horarios u otra información que usan esta aula."));
		} finally {
			dispatch(disableIdleLockSwitch());
		}
	};

	const borrarEdificio = async () => {
		if (!borrandoEdificio) return;
		setErrorBorrar(null);
		dispatch(enableIdleLockSwitch());
		try {
			await BuildingsService.removeBuildings(campusId, [borrandoEdificio.id]);
			setBorrandoEdificio(null);
			setVentanaEdificio(null);
			setAviso("Edificio Borrado");
			await cargar();
		} catch (e) {
			setErrorBorrar(motivoError(e, "No se pudo borrar: el edificio todavía tiene aulas.", "No se pudo borrar: el edificio todavía tiene aulas."));
		} finally {
			dispatch(disableIdleLockSwitch());
		}
	};

	// El clic en la fila abre la ventana; el «⋯» queda para lo secundario (borrar), solo si hay permiso.
	const menuAula = (a: Aula) => (pAulas.delete ? [{ texto: "Borrar", onClick: () => pedirBorrarAula(a), peligro: true }] : []);

	const categoriasDe = (a: Aula) => ((a.categories || []).length < 1 ? "-" : a.categories.map((c) => nombreCategoria(c.name)).join(", "));

	const tablaAulas = (lista: Aula[]) => {
		if (lista.length < 1) return <div className="calVacio" style={{ padding: 14 }}>Sin Aulas</div>;
		if (movil) {
			return (
				<div className="calTarjetas" style={{ paddingTop: 8 }}>
					{lista.map((a) => (
						<div key={a.id} className="calTarjeta" role="button" tabIndex={0} onClick={() => setVentanaAula(a)} onKeyDown={(e) => { if (e.key === "Enter") setVentanaAula(a); }}>
							<div className="calTarjetaArriba">
								<div style={{ minWidth: 0 }}><b>{a.name}</b></div>
								<div style={{ display: "flex", gap: 6, alignItems: "center", flexShrink: 0 }}>
									{inactiva(a) ? <Badge bg="oro" style={{ fontWeight: 500 }}>Inactiva</Badge> : null}
									<MenuFila opciones={menuAula(a)} />
								</div>
							</div>
							<div style={{ fontSize: 14, marginTop: 4 }}>Cupo {a.students_limit} - {a.session_min_hours} {Number(a.session_min_hours) === 1 ? "Hora" : "Horas"} Mín.</div>
							<div className="calSub">{categoriasDe(a)}</div>
						</div>
					))}
				</div>
			);
		}
		return (
			<table className="table table-hover calLista" style={{ fontSize: 15, marginBottom: 0 }}>
				<thead>
					<tr><th>Aula</th><th className="calNum" style={{ width: 90 }}>Cupo</th><th className="calNum" style={{ width: 190 }}>Horas Mín. por Sesión</th><th style={{ width: "34%" }}>Categorías</th><th className="calMenu" /></tr>
				</thead>
				<tbody>
					{lista.map((a) => (
						<tr key={a.id} onClick={() => setVentanaAula(a)}>
							<td>
								<b>{a.name}</b>{inactiva(a) ? <>&nbsp;<Badge bg="oro" style={{ fontWeight: 500 }}>Inactiva</Badge></> : null}
								{a.description ? <div className="calSub">{a.description}</div> : null}
							</td>
							<td className="calNum">{a.students_limit}</td>
							<td className="calNum">{a.session_min_hours}</td>
							<td>{categoriasDe(a)}</td>
							<td className="calMenu"><MenuFila opciones={menuAula(a)} /></td>
						</tr>
					))}
				</tbody>
			</table>
		);
	};

	const cuerpo = () => {
		if (!edificios) return <div className="loader" />;
		if (grupos.length < 1) return <div className="calVacio">{hayFiltros ? "Sin Aulas" : "Sin Edificios"}</div>;
		return grupos.map((g) => (
			<div className="calEdificio" key={g.edificio.id}>
				<div className="calEdificioTitulo">
					<h6>{g.edificio.name}</h6>
					<span className="calSub">{g.aulas.length === 1 ? "1 Aula" : g.aulas.length + " Aulas"}</span>
					{pEdificios.update || pEdificios.read ? (
						<button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => setVentanaEdificio(g.edificio)}>
							{pEdificios.update ? "Editar Edificio" : "Ver Edificio"}
						</button>
					) : null}
				</div>
				<div className="waScrollX" style={{ overflowX: "auto" }}>{tablaAulas(g.aulas)}</div>
			</div>
		));
	};

	// Alta de aula: en el edificio que se filtró, o el primero.
	const edificioInicial = filtroEdificios.length === 1 ? filtroEdificios[0] : (grupos[0]?.edificio.id ?? (edificios && edificios[0] ? edificios[0].id : 0));

	return (
		<div className="container-fluid waModulo calMod" style={{ textAlign: "left", padding: movil ? "10px 12px 0 12px" : "10px 24px 0 24px" }}>
			<div className="calEncabezado">
				<h4>Aulas</h4>
				<div className="calAcciones">
					{pEdificios.create ? (
						<button type="button" className="btn btn-outline-secondary waBotonGrande" onClick={() => setVentanaEdificio("nuevo")}>
							<FontAwesomeIcon icon={solid("plus")} />&nbsp;Nuevo Edificio
						</button>
					) : null}
					{pAulas.create && edificios && edificios.length > 0 ? (
						<button type="button" className="btn btn-primary waBotonGrande" onClick={() => setVentanaAula("nueva")}>
							<FontAwesomeIcon icon={solid("plus")} />&nbsp;Nueva Aula
						</button>
					) : null}
				</div>
			</div>

			{error ? <div className="alert alert-danger" style={{ fontSize: 15 }}>{error}</div> : null}
			{aviso ? <div className="alert alert-success" style={{ fontSize: 15, display: "flex", justifyContent: "space-between" }}>{aviso}<button type="button" className="btn-close" style={{ fontSize: 10 }} onClick={() => setAviso(null)} /></div> : null}

			<ul className="nav nav-pills waTabs calPestanas" role="tablist">
				<li className="nav-item"><button type="button" role="tab" aria-selected={pestana === "lista"} className={"nav-link btn btn-sm" + (pestana === "lista" ? " active" : "")} onClick={() => setPestana("lista")}>Lista de Aulas{aulas.length ? <span className="waTabCuenta">{aulas.length}</span> : null}</button></li>
				{verOcupacion ? <li className="nav-item"><button type="button" role="tab" aria-selected={pestana === "ocupacion"} className={"nav-link btn btn-sm" + (pestana === "ocupacion" ? " active" : "")} onClick={() => setPestana("ocupacion")}>Ocupación</button></li> : null}
			</ul>

			{pestana === "ocupacion" && verOcupacion ? (
				<Card>
					<div style={{ padding: movil ? "12px 12px 12px 12px" : "14px 18px 14px 18px" }}>
						<OcupacionAulas campusId={campusId} countryId={countryId} abreCalendario={abreCalendario} />
					</div>
				</Card>
			) : (
			<Card>
				<div style={{ padding: movil ? "12px 12px 12px 12px" : "14px 18px 14px 18px" }}>
					<BarraFiltros hayFiltros={hayFiltros} onLimpiar={() => { setQ(""); setFiltroEdificios([]); setFiltroCategorias([]); }}>
						<BuscadorBarra valor={q} onBuscar={setQ} placeholder="Buscar Aula" />
						<FiltroMulti titulo="Edificio" opciones={opcionesEdificio} elegidos={filtroEdificios} onChange={setFiltroEdificios} />
						<FiltroMulti titulo="Categoría" opciones={opcionesCategoria} elegidos={filtroCategorias} onChange={setFiltroCategorias} />
					</BarraFiltros>
					<div style={{ marginTop: 16 }}>{cuerpo()}</div>
				</div>
			</Card>
			)}
			<div className="waSobreDock" />

			{ventanaAula && edificios ? (
				<AulaVentana campusId={campusId} aula={ventanaAula === "nueva" ? null : ventanaAula} edificioInicial={edificioInicial} edificios={edificios}
					categorias={categorias} estatuses={estatuses} puedeEditar={ventanaAula === "nueva" ? !!pAulas.create : !!pAulas.update} puedeBorrar={!!pAulas.delete}
					onClose={() => setVentanaAula(null)} onGuardada={guardada} onBorrar={pedirBorrarAula} />
			) : null}
			{ventanaEdificio ? (
				<EdificioVentana campusId={campusId} edificio={ventanaEdificio === "nuevo" ? null : ventanaEdificio} puedeEditar={ventanaEdificio === "nuevo" ? !!pEdificios.create : !!pEdificios.update}
					puedeBorrar={!!pEdificios.delete} onClose={() => setVentanaEdificio(null)} onGuardado={guardada} onBorrar={pedirBorrarEdificio} />
			) : null}
			{borrandoAula ? <ConfirmarBorrado titulo="Borrar Aula" nombre={borrandoAula.name} error={errorBorrar} onCancelar={() => setBorrandoAula(null)} onBorrar={borrarAula} /> : null}
			{borrandoEdificio ? <ConfirmarBorrado titulo="Borrar Edificio" nombre={borrandoEdificio.name} error={errorBorrar} onCancelar={() => setBorrandoEdificio(null)} onBorrar={borrarEdificio} /> : null}
		</div>
	);
};

export default AulasController;
